import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Form, Input, Select, Button, Typography, Spin, Space, Upload, Row, Col, Badge } from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  FileExclamationOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title } = Typography;
const { Option } = Select;

const EditContentComplaints = () => {
  const { content_id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [fileList, setFileList] = useState([]);

  const fetchContentComplaintsDetails = useCallback(async () => {
    if (!content_id) {
      notify.error({ message: "Content complaint ID is required" });
      navigate("/content-compliance");
      return;
    }

    setLoadingData(true);
    try {
      const response = await masterService.getSingleContentComplaint(
        content_id
      );

      if (response.data && response.data.status) {
        const contentComplaintsData =
          response.data.data[0] || response.data.data;
        if (contentComplaintsData && contentComplaintsData.content_name) {
          form.setFieldsValue({
            content_name: contentComplaintsData.content_name,
            sequence_number: contentComplaintsData.sequence_number,
            is_active: contentComplaintsData.is_active || "Y",
            content_url: contentComplaintsData.content_url, // Set the existing content_url
          });

          // Set file list if there's an existing file
          if (contentComplaintsData.content_url) {
            setFileList([
              {
                uid: "-1",
                name: "existing-file.pdf",
                status: "done",
                url: contentComplaintsData.content_url,
              },
            ]);
          }
        } else {
          notify.error({ message: "Content complaint data not found" });
          navigate("/content-compliance");
        }
      } else {
        notify.error({
          message:
            response.data?.message ||
            "Failed to fetch content complaint details",
        });
        navigate("/content-compliance");
      }
    } catch (error) {
      console.error("Error fetching content complaint details:", error);
      notify.error({ message: "Error fetching content complaint details" });
      navigate("/content-compliance");
    } finally {
      setLoadingData(false);
    }
  }, [content_id, form, navigate]);

  useEffect(() => {
    setPageTitle("Edit Content Compliance");
    fetchContentComplaintsDetails();
  }, [fetchContentComplaintsDetails, setPageTitle]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        content_id: content_id,
      };

      const response = await masterService.updateContentComplaint(
        content_id,
        payload
      );

      if (response.data && response.data.status) {
        notify.success({
          message:
            response.data.message || "Content complaint updated successfully",
        });
        
        navigate("/content-compliance");
      } else {
        notify.error({
          message:
            response.data.message || "Failed to update content complaint",
        });
      }
    } catch (error) {
      console.error("Error updating content complaint:", error);
      notify.error({
        message: error?.data?.message || "Error updating content complaint",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setFileList([]);
  };

  const handleGoBack = () => {
    navigate("/content-compliance");
  };

  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append("pdf", file);

    try {
      const response = await masterService.uploadPdf(formData);
      console.log("responsepdf===>", response.data.data.s3_key);
      if (response.data && response.data.status) {
        form.setFieldsValue({
          content_url: response.data.data.s3_key,
        });
        notify.success({
          message: response.data.message || "File uploaded successfully",
        });
        return false; // Prevent default upload behavior
      } else {
        notify.error({
          message: response.data.message || "Failed to upload file",
        });
        return false;
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      notify.error({ message: error.data.message || "Error uploading file" });
      return false;
    }
  };

  const uploadProps = {
    beforeUpload: handleFileUpload,
    fileList,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    accept: ".pdf",
    maxCount: 1,
  };

  return (
    <div className="min-h-screen">
      <Spin spinning={loadingData}>
        <Card
        >
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleSubmit}
            className="space-y-6"
          >
            {/* Content Complaints Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <FileExclamationOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Content Complaints Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Content Name
                      </span>
                    }
                    name="content_name"
                    rules={[
                      { required: true, message: "Please enter content name" },
                    ]}
                  >
                    <Input
                      placeholder="Enter content name"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Sequence
                      </span>
                    }
                    name="sequence_number"
                    rules={[
                      {
                        required: true,
                        message: "Please enter sequence number",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter sequence number"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="is_active"
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Hidden field to store the uploaded PDF URL string */}
              <Form.Item
                name="content_url"
                rules={[{ required: true, message: "Please upload a PDF" }]}
                style={{ display: "none" }}
              >
                <Input />
              </Form.Item>

              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Upload PDF
                      </span>
                    }
                  >
                    <Upload {...uploadProps}>
                      <Button icon={<UploadOutlined />} className="rounded-lg">
                        Upload PDF
                      </Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Action Buttons */}
            <div
              className="flex justify-end gap-4 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space size="middle">
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleGoBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {loading ? "Updating..." : "Update Content Complaints"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default EditContentComplaints;
